import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Button } from "react-bootstrap";
import { FaChevronLeft, FaInfoCircle, FaLink } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useClinicOptions from "../hooks/useClinicOptions";
import useDoctorOptions from "../hooks/useDoctorOptions";
import GuardianMatchBanner from "../components/CoCM/GuardianMatchBanner";
import {
  formatPhoneInput,
  getPhoneDigits,
} from "../utils/providerProfileFields";
import "./AddNewPatient.css";

const formatAppUserDisplayId = (match) => {
  if (match?.userDisplayId) return match.userDisplayId;
  if (match?.userAutoGenId != null && match.userAutoGenId !== "") {
    return `USR-${String(match.userAutoGenId).padStart(4, "0")}`;
  }
  const raw = String(match?.userId || "");
  if (raw.length >= 4) return `USR-${raw.slice(-4).toUpperCase()}`;
  return "USR-----";
};

const formatAppUserDisplayName = (match) => {
  const name = `${match?.firstName || ""} ${match?.lastName || ""}`.trim();
  return name || "App user";
};

const calculateAgeFromDob = (dob) => {
  if (!dob) return null;
  const parsed = new Date(dob);
  if (Number.isNaN(parsed.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDiff = today.getMonth() - parsed.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.getDate())) {
    age -= 1;
  }
  return age;
};

const RELATIONSHIP_OPTIONS = [
  "Mother",
  "Father",
  "Guardian",
  "Grandparent",
  "Other",
];

const emptyGuardianFields = {
  guardianEmail: "",
  guardianPhone: "",
  guardianFirstName: "",
  guardianLastName: "",
  guardianRelationship: "",
};

const AddNewPatient = () => {
  const navigate = useNavigate();
  const { clinics: clinicOptions } = useClinicOptions(true);
  const [careManagers, setCareManagers] = useState([]);
  const [psychiatricConsultants, setPsychiatricConsultants] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [isAlreadyCoCMPatient, setIsAlreadyCoCMPatient] = useState(false);
  const [alreadyLinkedSamePatientMessage, setAlreadyLinkedSamePatientMessage] =
    useState("");
  /** Existing Numente app user matched by email (not yet a CoCM patient). */
  const [matchedAppUser, setMatchedAppUser] = useState(null);
  /** null | 'link' | 'ignore' — admin choice on the match card */
  const [appLinkDecision, setAppLinkDecision] = useState(null);
  const [reviewRequesting, setReviewRequesting] = useState(false);
  const [reviewRequested, setReviewRequested] = useState(false);
  /** App user already linked to another CoCM patient */
  const [linkConflict, setLinkConflict] = useState(null);
  const [saving, setSaving] = useState(false);
  const lastLookedUpEmailRef = useRef("");
  const lastLookedUpGuardianEmailRef = useRef("");
  const numenteAccessTouchedRef = useRef(false);
  const [guardianMatch, setGuardianMatch] = useState(null);
  const [guardianAccepted, setGuardianAccepted] = useState(false);
  const [formData, setFormData] = useState({
    patientId: "",
    firstName: "",
    lastName: "",
    dob: "",
    phone: "",
    email: "",
    status: "",
    enrollmentDate: "",
    clinic: "",
    pcp: "",
    careManager: "",
    careManagerId: "",
    psychiatricConsultant: "",
    psychiatricConsultantId: "",
    numenteAccess: "patient",
    ...emptyGuardianFields,
  });
  const { doctors: doctorOptions } = useDoctorOptions(true, formData.clinic);

  useEffect(() => {
    const token = localStorage.getItem("user_id");

    const fetchStaff = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-cocm-staff`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setCareManagers(response.data.data.careManagers || []);
        setPsychiatricConsultants(
          response.data.data.psychiatricConsultants || [],
        );
      }
    };

    const fetchNextPatientId = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/next-cocm-patient-id`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setFormData((prev) => ({
          ...prev,
          patientId: response.data.data.patientId,
        }));
      }
    };

    const loadPage = async () => {
      setPageLoading(true);
      try {
        await Promise.all([fetchStaff(), fetchNextPatientId()]);
      } catch (error) {
        console.error("Error loading add patient page:", error);
      } finally {
        setPageLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "numenteAccess") {
      numenteAccessTouchedRef.current = true;
    }
    if (name === "email") {
      // Email change only resets match/lookup UI state — never wipe admin-entered fields.
      setIsAlreadyCoCMPatient(false);
      setAlreadyLinkedSamePatientMessage("");
      setMatchedAppUser(null);
      setAppLinkDecision(null);
      setLinkConflict(null);
      setReviewRequested(false);
      lastLookedUpEmailRef.current = "";
      setFormData((prev) => ({
        ...prev,
        email: value,
      }));
      return;
    }
    if (name === "guardianEmail") {
      setGuardianMatch(null);
      setGuardianAccepted(false);
      lastLookedUpGuardianEmailRef.current = "";
      setFormData((prev) => ({ ...prev, guardianEmail: value }));
      return;
    }
    if (name === "phone" || name === "guardianPhone") {
      setFormData((prev) => ({ ...prev, [name]: formatPhoneInput(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const age = calculateAgeFromDob(formData.dob);
    if (age == null) return;

    if (age >= 18) {
      numenteAccessTouchedRef.current = false;
      setGuardianMatch(null);
      setGuardianAccepted(false);
      lastLookedUpGuardianEmailRef.current = "";
      setFormData((prev) => ({
        ...prev,
        numenteAccess: "patient",
        ...emptyGuardianFields,
      }));
      return;
    }

    if (numenteAccessTouchedRef.current) return;
    setFormData((prev) => ({
      ...prev,
      numenteAccess: "guardian",
    }));
  }, [formData.dob]);

  const logMatchEvent = useCallback(async (payload) => {
    try {
      const token = localStorage.getItem("user_id");
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/cocm-app-user-match-event`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return true;
    } catch (error) {
      console.error("Error logging CoCM match event:", error);
      return false;
    }
  }, []);

  const lookupPatientByEmail = useCallback(async (emailValue) => {
    const email = (emailValue || "").trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setIsAlreadyCoCMPatient(false);
      setAlreadyLinkedSamePatientMessage("");
      setMatchedAppUser(null);
      setAppLinkDecision(null);
      setLinkConflict(null);
      setReviewRequested(false);
      return;
    }

    if (email === lastLookedUpEmailRef.current) return;
    lastLookedUpEmailRef.current = email;

    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/lookup-patient-by-email`,
        {
          params: { email },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.data?.success) return;

      if (!response.data.exists) {
        setIsAlreadyCoCMPatient(false);
        setAlreadyLinkedSamePatientMessage("");
        setMatchedAppUser(null);
        setAppLinkDecision(null);
        setLinkConflict(null);
        setReviewRequested(false);
        // No match — keep whatever the admin already entered on the form
        return;
      }

      const profile = response.data.data || {};

      // Already a CoCM registry patient — warn only; do NOT map into form fields
      if (response.data.linked) {
        setIsAlreadyCoCMPatient(true);
        setAlreadyLinkedSamePatientMessage(
          response.data.alreadyLinkedToSamePatient
            ? response.data.message ||
                "This app user is already linked to this registry patient."
            : "",
        );
        setMatchedAppUser(null);
        setAppLinkDecision(null);
        setLinkConflict(null);
        setReviewRequested(false);
        return;
      }

      // Conflict: app user already linked to another CoCM patient — no field mapping
      if (response.data.conflict) {
        setIsAlreadyCoCMPatient(false);
        setAlreadyLinkedSamePatientMessage("");
        setMatchedAppUser(profile);
        setAppLinkDecision(null);
        setLinkConflict({
          message: response.data.message,
          linkedCoCMPatient: profile.linkedCoCMPatient || null,
        });
        setReviewRequested(false);
        // Match Found audit (no CoCM patient id for the *new* draft yet)
        logMatchEvent({
          eventType: "match_found",
          email,
          matchedAppUserId: profile.userId,
        });
        return;
      }

      // Existing Numente app user — show match card; wait for Link/Ignore (no autofill yet)
      setIsAlreadyCoCMPatient(false);
      setAlreadyLinkedSamePatientMessage("");
      setMatchedAppUser(profile);
      setAppLinkDecision(null);
      setLinkConflict(null);
      setReviewRequested(false);
      logMatchEvent({
        eventType: "match_found",
        email,
        matchedAppUserId: profile.userId,
      });
    } catch (error) {
      console.error("Error looking up patient by email:", error);
      setIsAlreadyCoCMPatient(false);
      setAlreadyLinkedSamePatientMessage("");
      setMatchedAppUser(null);
      setAppLinkDecision(null);
      setLinkConflict(null);
      setReviewRequested(false);
      lastLookedUpEmailRef.current = "";
    }
  }, [logMatchEvent]);

  const handleEmailBlur = (e) => {
    lookupPatientByEmail(e.target.value);
  };

  const lookupGuardianByEmail = useCallback(async (emailValue) => {
    const email = (emailValue || "").trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setGuardianMatch(null);
      setGuardianAccepted(false);
      return;
    }
    if (email === lastLookedUpGuardianEmailRef.current) return;
    lastLookedUpGuardianEmailRef.current = email;

    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/lookup-guardian`,
        {
          params: { email },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.data?.success) return;
      setGuardianMatch(response.data.data || null);
      setGuardianAccepted(false);
    } catch (error) {
      console.error("Error looking up guardian:", error);
      setGuardianMatch(null);
      setGuardianAccepted(false);
      lastLookedUpGuardianEmailRef.current = "";
    }
  }, []);

  const handleGuardianEmailBlur = (e) => {
    lookupGuardianByEmail(e.target.value);
  };

  const handleUseGuardian = () => {
    if (!guardianMatch) return;
    setFormData((prev) => ({
      ...prev,
      guardianFirstName: prev.guardianFirstName?.trim()
        ? prev.guardianFirstName
        : guardianMatch.firstName || "",
      guardianLastName: prev.guardianLastName?.trim()
        ? prev.guardianLastName
        : guardianMatch.lastName || "",
      guardianPhone: prev.guardianPhone?.trim()
        ? prev.guardianPhone
        : formatPhoneInput(guardianMatch.phone || ""),
      guardianRelationship: prev.guardianRelationship || guardianMatch.relationship || "",
    }));
    setGuardianAccepted(true);
  };

  const handleLinkAppAccount = () => {
    if (!matchedAppUser || linkConflict) return;
    // Link the account, but never overwrite fields the admin already filled.
    // Only backfill blank personal fields from the matched app user.
    setFormData((prev) => ({
      ...prev,
      firstName: prev.firstName?.trim()
        ? prev.firstName
        : matchedAppUser.firstName || "",
      lastName: prev.lastName?.trim()
        ? prev.lastName
        : matchedAppUser.lastName || "",
      dob: prev.dob?.trim() ? prev.dob : matchedAppUser.dob || "",
      phone: prev.phone?.trim()
        ? prev.phone
        : formatPhoneInput(matchedAppUser.phone || ""),
    }));
    setAppLinkDecision("link");
  };

  const handleIgnoreAndContinue = () => {
    if (!matchedAppUser) return;
    // Do not link the app account now (access-code link later), but map the
    // existing app user's saved personal fields into the CoCM form.
    setFormData((prev) => ({
      ...prev,
      firstName: matchedAppUser.firstName || prev.firstName || "",
      lastName: matchedAppUser.lastName || prev.lastName || "",
      dob: matchedAppUser.dob || prev.dob || "",
      phone: formatPhoneInput(matchedAppUser.phone || prev.phone || ""),
    }));
    setAppLinkDecision("ignore");
    setLinkConflict(null);
  };

  const handleViewExistingLink = () => {
    const id = linkConflict?.linkedCoCMPatient?.id;
    if (id) navigate(`/patient-info/${id}`);
  };

  const handleCancelConflict = () => {
    setMatchedAppUser(null);
    setLinkConflict(null);
    setAppLinkDecision(null);
    setReviewRequested(false);
    setFormData((prev) => ({ ...prev, email: "" }));
    lastLookedUpEmailRef.current = "";
  };

  const handleRequestAdminReview = async () => {
    if (!matchedAppUser?.userId || !linkConflict?.linkedCoCMPatient?.id) {
      alert("Missing linked patient details for review request.");
      return;
    }
    if (reviewRequested || reviewRequesting) return;

    setReviewRequesting(true);
    const ok = await logMatchEvent({
      eventType: "review_requested",
      email: formData.email,
      matchedAppUserId: matchedAppUser.userId,
      linkedCoCMPatientId: linkConflict.linkedCoCMPatient.id,
      careManagerId: formData.careManagerId || undefined,
      careManager: formData.careManager || undefined,
    });
    setReviewRequesting(false);

    if (ok) {
      setReviewRequested(true);
      alert(
        "Admin review requested. A review activity was logged on the existing linked CoCM patient.",
      );
    } else {
      alert("Failed to request admin review. Please try again.");
    }
  };

  const handleCareManagerChange = (e) => {
    const selectedId = e.target.value;
    const selectedCM = careManagers.find(
      (cm) => cm.id.toString() === selectedId,
    );
    setFormData((prev) => ({
      ...prev,
      careManagerId: selectedId,
      careManager: selectedCM ? selectedCM.userName : "",
    }));
  };

  const handlePsychiatricConsultantChange = (e) => {
    const selectedId = e.target.value;
    const selectedPC = psychiatricConsultants.find(
      (pc) => pc.id.toString() === selectedId,
    );
    setFormData((prev) => ({
      ...prev,
      psychiatricConsultantId: selectedId,
      psychiatricConsultant: selectedPC ? selectedPC.userName : "",
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.patientId) {
      alert("Patient ID is not ready yet. Please wait and try again.");
      return;
    }
    if (!formData.clinic) {
      alert("Clinic is required to generate an access code.");
      return;
    }
    if (!formData.careManagerId) {
      alert("Care Manager is required to generate an access code.");
      return;
    }

    const age = calculateAgeFromDob(formData.dob);
    const minor = age != null && age < 18;
    const accessMode = minor
      ? String(formData.numenteAccess || "guardian").toLowerCase()
      : "patient";
    const guardianAccessRequired =
      minor && (accessMode === "guardian" || accessMode === "both");
    const patientAccessRequired =
      !minor || accessMode === "patient" || accessMode === "both";
    const hasGuardianEmail = Boolean(formData.guardianEmail?.trim());
    // Parent/Guardian access requires guardian; Patient access may optionally include one.
    const includeGuardian = guardianAccessRequired || (minor && hasGuardianEmail);

    if (patientAccessRequired && !formData.email?.trim()) {
      alert("Patient email is required for the selected Numente Access.");
      return;
    }
    if (
      patientAccessRequired &&
      getPhoneDigits(formData.phone).length !== 10
    ) {
      alert("Enter a valid 10-digit patient phone number.");
      return;
    }
    if (
      !patientAccessRequired &&
      getPhoneDigits(formData.phone).length > 0 &&
      getPhoneDigits(formData.phone).length !== 10
    ) {
      alert("Enter a valid 10-digit patient phone number.");
      return;
    }

    if (includeGuardian) {
      if (!formData.guardianEmail?.trim()) {
        alert("Guardian email is required.");
        return;
      }
      if (getPhoneDigits(formData.guardianPhone).length !== 10) {
        alert("Enter a valid 10-digit guardian phone number.");
        return;
      }
      if (!formData.guardianRelationship?.trim()) {
        alert("Guardian relationship is required.");
        return;
      }
      if (
        guardianMatch &&
        Number(guardianMatch.state) > 1 &&
        !guardianAccepted
      ) {
        alert("Select the matched parent/guardian before saving.");
        return;
      }
      const patientEmail = formData.email?.trim().toLowerCase();
      const guardianEmail = formData.guardianEmail?.trim().toLowerCase();
      if (patientEmail && guardianEmail && patientEmail === guardianEmail) {
        alert(
          "The parent/guardian cannot be saved as the CoCM patient. Enter the child's email for the patient and a different email for the guardian.",
        );
        return;
      }
    }
    if (patientAccessRequired && isAlreadyCoCMPatient) {
      alert("This email is already registered as a CoCM patient.");
      return;
    }
    if (patientAccessRequired && linkConflict) {
      alert(
        linkConflict.message ||
          "This app user is already linked to another CoCM Registry patient. Please review before linking.",
      );
      return;
    }
    if (patientAccessRequired && matchedAppUser && !appLinkDecision) {
      alert(
        "An existing app user matches this email. Choose Link App Account or Ignore and Continue before saving.",
      );
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("user_id");
      const payload = {
        ...formData,
        phone: getPhoneDigits(formData.phone),
        guardianPhone: getPhoneDigits(formData.guardianPhone),
        email: formData.email?.trim() || "",
        linkAppAccount:
          patientAccessRequired &&
          Boolean(matchedAppUser) &&
          appLinkDecision === "link",
        ignoreAppUserMatch:
          patientAccessRequired &&
          Boolean(matchedAppUser) &&
          appLinkDecision === "ignore",
        matchedAppUserId:
          patientAccessRequired &&
          matchedAppUser &&
          (appLinkDecision === "link" || appLinkDecision === "ignore")
            ? matchedAppUser?.userId || null
            : null,
        numenteAccess: accessMode,
        guardian: includeGuardian
          ? {
              email: formData.guardianEmail,
              firstName: formData.guardianFirstName,
              lastName: formData.guardianLastName,
              phone: getPhoneDigits(formData.guardianPhone),
              relationship: formData.guardianRelationship,
              useExisting: guardianAccepted,
            }
          : null,
      };

      if (!includeGuardian) {
        Object.assign(payload, emptyGuardianFields);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/add-patient`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const createdPatientId =
        response.data?.data?._id ||
        response.data?.data?.id ||
        response.data?.data?.userId ||
        null;

      if (createdPatientId) {
        navigate(`/patient-info/${createdPatientId}`);
      } else {
        navigate("/registry-cocm");
      }
    } catch (error) {
      console.error("Error saving patient data:", error);
      alert(error.response?.data?.message || "Failed to save patient data");
    } finally {
      setSaving(false);
    }
  };

  const isMinor =
    calculateAgeFromDob(formData.dob) != null &&
    calculateAgeFromDob(formData.dob) < 18;
  const accessMode = isMinor
    ? String(formData.numenteAccess || "guardian").toLowerCase()
    : "patient";
  const showNumenteAccess = isMinor;
  // Minors always show Parent/Guardian Info so admins can optionally add it
  // when Numente Access is "Patient".
  const showGuardian = isMinor;
  const guardianRequired =
    isMinor && (accessMode === "guardian" || accessMode === "both");
  const patientEmailRequired =
    !isMinor || accessMode === "patient" || accessMode === "both";
  const wantsPatientAccess = patientEmailRequired;
  const hasGuardianEmail = Boolean(String(formData.guardianEmail || "").trim());
  const includeGuardianOptional = showGuardian && hasGuardianEmail;
  const showPatientAppMatch =
    wantsPatientAccess ||
    Boolean(matchedAppUser) ||
    Boolean(linkConflict) ||
    isAlreadyCoCMPatient;
  const guardianNeedsAccept =
    (guardianRequired || includeGuardianOptional) &&
    guardianMatch &&
    Number(guardianMatch.state) > 1 &&
    !guardianAccepted;
  const guardianNeedsRelationship =
    (guardianRequired || includeGuardianOptional) &&
    !String(formData.guardianRelationship || "").trim();
  const guardianNeedsEmail =
    guardianRequired && !String(formData.guardianEmail || "").trim();
  const patientPhoneDigits = getPhoneDigits(formData.phone);
  const guardianPhoneDigits = getPhoneDigits(formData.guardianPhone);
  const guardianNeedsPhone =
    (guardianRequired || includeGuardianOptional) &&
    guardianPhoneDigits.length !== 10;
  const patientNeedsEmail =
    patientEmailRequired && !String(formData.email || "").trim();
  const patientNeedsPhone = wantsPatientAccess
    ? patientPhoneDigits.length !== 10
    : patientPhoneDigits.length > 0 && patientPhoneDigits.length !== 10;
  const patientNeedsAppDecision =
    wantsPatientAccess && matchedAppUser && !appLinkDecision;
  const canSave =
    !pageLoading &&
    !saving &&
    Boolean(formData.patientId) &&
    !patientNeedsEmail &&
    !patientNeedsPhone &&
    !(wantsPatientAccess && isAlreadyCoCMPatient) &&
    !(wantsPatientAccess && linkConflict) &&
    !guardianNeedsAccept &&
    !guardianNeedsEmail &&
    !guardianNeedsPhone &&
    !guardianNeedsRelationship &&
    !patientNeedsAppDecision;

  // Any admin-entered value (patientId and numenteAccess are auto-set).
  const hasUnsavedChanges = Object.entries(formData).some(
    ([key, value]) =>
      key !== "patientId" &&
      key !== "numenteAccess" &&
      String(value || "").trim() !== "",
  );

  const accessOptions = [
    { value: "guardian", label: "Parent/Guardian" },
    { value: "patient", label: "Patient" },
    { value: "both", label: "Both" },
  ];

  const renderPatientEmailMatchCards = () => (
    <>
      {matchedAppUser && appLinkDecision == null && !linkConflict && (
        <div
          className="mt-3"
          style={{
            backgroundColor: "#EBF5FB",
            border: "1px solid #BFDBFE",
            borderRadius: "12px",
            padding: "14px 16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#1E3A8A",
              fontWeight: "700",
              fontSize: "14px",
              marginBottom: "6px",
            }}
          >
            <FaInfoCircle size={15} />
            Existing app user found
          </div>
          <div style={{ fontSize: "13px", color: "#334155", marginBottom: "12px" }}>
            {formatAppUserDisplayName(matchedAppUser)} (
            {formatAppUserDisplayId(matchedAppUser)}) matches this email.
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={handleLinkAppAccount}
              style={{
                backgroundColor: "#2C64EF",
                border: "none",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "13px",
                padding: "8px 14px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FaLink size={12} />
              Link App Account
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              onClick={handleIgnoreAndContinue}
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #93C5FD",
                color: "#2C64EF",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "13px",
                padding: "8px 14px",
              }}
            >
              Ignore and Continue
            </Button>
          </div>
        </div>
      )}

      {matchedAppUser && linkConflict && appLinkDecision == null && (
        <div
          className="mt-3"
          style={{
            backgroundColor: "#FEF3C7",
            border: "1px solid #FCD34D",
            borderRadius: "12px",
            padding: "14px 16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#92400E",
              fontWeight: "700",
              fontSize: "14px",
              marginBottom: "6px",
            }}
          >
            <FaInfoCircle size={15} />
            Existing app user already linked
          </div>
          <div style={{ fontSize: "13px", color: "#78350F", marginBottom: "8px" }}>
            {linkConflict.message ||
              "This app user is already linked to another CoCM Registry patient. Please review before linking."}
          </div>
          {linkConflict.linkedCoCMPatient && (
            <div style={{ fontSize: "12px", color: "#92400E", marginBottom: "12px" }}>
              Existing link: {linkConflict.linkedCoCMPatient.name}
              {linkConflict.linkedCoCMPatient.patientId
                ? ` (${linkConflict.linkedCoCMPatient.patientId})`
                : ""}
            </div>
          )}
          <div className="d-flex flex-wrap gap-2">
            <Button type="button" onClick={handleViewExistingLink} disabled={!linkConflict.linkedCoCMPatient?.id} style={{ backgroundColor: "#92400E", border: "none", borderRadius: "8px", fontWeight: "600", fontSize: "13px", padding: "8px 14px" }}>
              View Existing Link
            </Button>
            <Button type="button" onClick={handleCancelConflict} style={{ backgroundColor: "#FFFFFF", border: "1px solid #F59E0B", color: "#92400E", borderRadius: "8px", fontWeight: "600", fontSize: "13px", padding: "8px 14px" }}>
              Cancel
            </Button>
            <Button type="button" onClick={handleRequestAdminReview} disabled={reviewRequesting || reviewRequested} style={{ backgroundColor: "#FFFFFF", border: "1px solid #F59E0B", color: "#92400E", borderRadius: "8px", fontWeight: "600", fontSize: "13px", padding: "8px 14px", opacity: reviewRequested ? 0.7 : 1 }}>
              {reviewRequested ? "Review Requested" : reviewRequesting ? "Requesting..." : "Request Admin Review"}
            </Button>
          </div>
        </div>
      )}

      {appLinkDecision === "ignore" && matchedAppUser && (
        <div className="mt-3" style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "12px 14px", fontSize: "13px", color: "#475569" }}>
          Match ignored. This CoCM patient will be created <strong>without</strong> linking {formatAppUserDisplayName(matchedAppUser)} ({formatAppUserDisplayId(matchedAppUser)}).
        </div>
      )}

      {isAlreadyCoCMPatient && (
        <div className="mt-3" style={{ backgroundColor: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: "12px", padding: "12px 14px", fontSize: "13px", color: "#92400E", fontWeight: "600" }}>
          {alreadyLinkedSamePatientMessage || "This email is already registered as a CoCM patient. You cannot create a duplicate registry patient."}
        </div>
      )}
    </>
  );

  return (
    <div className="app-page add-patient-page">
      <div className="add-patient-header">
        <button
          type="button"
          className="add-patient-back"
          aria-label="Back"
          onClick={() => navigate("/registry-cocm")}
        >
          <FaChevronLeft size={14} />
        </button>
        <h2 className="add-patient-title">Add New Patient</h2>
      </div>

      {!pageLoading && isMinor && (
        <div className="add-patient-minor-banner">
          <strong>Minor Patient</strong>
          <span>
            This patient is under 18. Please review and complete the appropriate parent/guardian and account access information.
          </span>
        </div>
      )}

      <div className="add-patient-scroll">
        <div className="bg-white rounded shadow-sm add-patient-shell">
          {pageLoading ? (
            <div className="add-patient-body d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "280px" }}>
              <div className="spinner-border text-success mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span style={{ fontSize: "14px", color: "#6B7280" }}>Preparing patient form…</span>
            </div>
          ) : (
            <div className="add-patient-body">
              <Form onSubmit={handleSave}>
                <section className="add-patient-section">
                  <h3 className="add-patient-section-title">Patient Info</h3>
                  <p className="add-patient-section-subtitle">
                    Info for individual receiving care.
                  </p>

                  <div className="add-patient-grid-2">
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">First Name</Form.Label>
                      <Form.Control type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} />
                    </Form.Group>
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">Last Name</Form.Label>
                      <Form.Control type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} />
                    </Form.Group>
                  </div>

                  <div className="add-patient-grid-2 add-patient-grid-spaced">
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">Date of Birth</Form.Label>
                      <Form.Control type="date" name="dob" value={formData.dob} onChange={handleChange} />
                    </Form.Group>
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">Patient ID</Form.Label>
                      <Form.Control type="text" name="patientId" value={formData.patientId} readOnly disabled />
                    </Form.Group>
                  </div>

                  {showNumenteAccess ? (
                    <div className="add-patient-access">
                      <div className="add-patient-label">Numente Access</div>
                      <div className="add-patient-radios">
                        {accessOptions.map((option) => (
                          <label key={option.value} className="add-patient-radio">
                            <input
                              type="radio"
                              name="numenteAccess"
                              value={option.value}
                              checked={formData.numenteAccess === option.value}
                              onChange={handleChange}
                            />
                            <span className="add-patient-radio-dot" />
                            {option.label}
                          </label>
                        ))}
                      </div>
                      <div className="add-patient-help">
                        Determines who receives the Numente invitation and can access this patient’s account.
                      </div>
                    </div>
                  ) : null}

                  <div className="add-patient-grid-2 add-patient-grid-spaced">
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">
                        Patient Email
                        {patientEmailRequired ? (
                          <span className="add-patient-required"> *</span>
                        ) : null}
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="Enter email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleEmailBlur}
                        required={patientEmailRequired}
                      />
                      {showPatientAppMatch && renderPatientEmailMatchCards()}
                      {!showPatientAppMatch && isAlreadyCoCMPatient && renderPatientEmailMatchCards()}
                    </Form.Group>
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">
                        Patient Phone
                        {wantsPatientAccess ? (
                          <span className="add-patient-required"> *</span>
                        ) : null}
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        placeholder="(XXX) XXX-XXXX"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        value={formData.phone}
                        onChange={handleChange}
                        required={wantsPatientAccess}
                      />
                    </Form.Group>
                  </div>
                </section>

                {showGuardian && (
                  <section className="add-patient-section">
                    <h3 className="add-patient-section-title">Parent/Guardian Info</h3>
                    {!guardianRequired ? (
                      <p className="add-patient-section-subtitle">
                        Optional. Add parent/guardian details if available.
                      </p>
                    ) : null}
                    <div className="add-patient-grid-2">
                      <Form.Group className="add-patient-field">
                        <Form.Label className="add-patient-label">
                          Email
                          {guardianRequired ? (
                            <span className="add-patient-required"> *</span>
                          ) : null}
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="guardianEmail"
                          placeholder="Enter email"
                          value={formData.guardianEmail}
                          onChange={handleChange}
                          onBlur={handleGuardianEmailBlur}
                          required={guardianRequired}
                        />
                      </Form.Group>
                      <Form.Group className="add-patient-field">
                        <Form.Label className="add-patient-label">
                          Phone
                          {guardianRequired || includeGuardianOptional ? (
                            <span className="add-patient-required"> *</span>
                          ) : null}
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="guardianPhone"
                          placeholder="(XXX) XXX-XXXX"
                          inputMode="numeric"
                          autoComplete="tel-national"
                          value={formData.guardianPhone}
                          onChange={handleChange}
                          required={guardianRequired || includeGuardianOptional}
                        />
                      </Form.Group>
                    </div>

                    {guardianMatch && (
                      <div className="add-patient-match">
                        <GuardianMatchBanner
                          match={guardianMatch}
                          accepted={guardianAccepted}
                          onUse={handleUseGuardian}
                        />
                      </div>
                    )}

                    <div className="add-patient-grid-3 add-patient-grid-spaced">
                      <Form.Group className="add-patient-field">
                        <Form.Label className="add-patient-label">First Name</Form.Label>
                        <Form.Control type="text" name="guardianFirstName" placeholder="First Name" value={formData.guardianFirstName} onChange={handleChange} />
                      </Form.Group>
                      <Form.Group className="add-patient-field">
                        <Form.Label className="add-patient-label">Last Name</Form.Label>
                        <Form.Control type="text" name="guardianLastName" placeholder="Last Name" value={formData.guardianLastName} onChange={handleChange} />
                      </Form.Group>
                      <Form.Group className="add-patient-field">
                        <Form.Label className="add-patient-label">
                          Relationship
                          {guardianRequired || includeGuardianOptional ? (
                            <span className="add-patient-required"> *</span>
                          ) : null}
                        </Form.Label>
                        <Form.Select
                          name="guardianRelationship"
                          value={formData.guardianRelationship}
                          onChange={handleChange}
                          required={guardianRequired || includeGuardianOptional}
                        >
                          <option value="">Select</option>
                          {RELATIONSHIP_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </section>
                )}

                <section className="add-patient-section">
                  <h3 className="add-patient-section-title">CoCM Info</h3>
                  <div className="add-patient-grid-2 add-patient-grid-spaced">
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">Enrollment Date</Form.Label>
                      <Form.Control type="date" name="enrollmentDate" value={formData.enrollmentDate} onChange={handleChange} />
                    </Form.Group>
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">Status</Form.Label>
                      <Form.Select name="status" value={formData.status} onChange={handleChange}>
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Intake">Intake</option>
                        <option value="Monitoring">Monitoring</option>
                        <option value="Outreach">Outreach</option>
                        <option value="Inactive">Inactive</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">Clinic</Form.Label>
                      <Form.Select name="clinic" value={formData.clinic} onChange={handleChange} required>
                        <option value="">Select Clinic</option>
                        {clinicOptions.map((clinic) => (
                          <option key={clinic.id} value={clinic.name}>{clinic.name}</option>
                        ))}
                        {formData.clinic && !clinicOptions.some((c) => c.name === formData.clinic) && (
                          <option value={formData.clinic}>{formData.clinic}</option>
                        )}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">PCP</Form.Label>
                      <Form.Select name="pcp" value={formData.pcp} onChange={handleChange}>
                        <option value="">Select PCP</option>
                        {doctorOptions.map((doctor) => (
                          <option key={doctor.id} value={doctor.name}>{doctor.name}</option>
                        ))}
                        {formData.pcp && !doctorOptions.some((d) => d.name === formData.pcp) && (
                          <option value={formData.pcp}>{formData.pcp}</option>
                        )}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">Care Manager</Form.Label>
                      <Form.Select name="careManagerId" value={formData.careManagerId} onChange={handleCareManagerChange} required>
                        <option value="">Select Care Manager</option>
                        {careManagers.map((cm) => (
                          <option key={cm.id} value={cm.id}>{cm.userName}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="add-patient-field">
                      <Form.Label className="add-patient-label">Psychiatric Consultant</Form.Label>
                      <Form.Select name="psychiatricConsultantId" value={formData.psychiatricConsultantId || ""} onChange={handlePsychiatricConsultantChange}>
                        <option value="">Select Psychiatric Consultant</option>
                        {psychiatricConsultants.map((pc) => (
                          <option key={pc.id} value={pc.id}>{pc.userName}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </section>
              </Form>
            </div>
          )}
        </div>
      </div>

      {!pageLoading && (
        <div className="add-patient-footer">
          {hasUnsavedChanges ? (
            <span className="add-patient-footer-note">Unsaved changes</span>
          ) : null}
          <div className="add-patient-footer-actions">
            <button type="button" className="add-patient-cancel" onClick={() => navigate("/registry-cocm")}>
              Cancel
            </button>
            <button type="button" className="add-patient-save" onClick={handleSave} disabled={!canSave}>
              {saving ? "Saving…" : "Save Patient"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddNewPatient;
